import { useEffect, useMemo, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Seo } from '@/components/marketing/Seo';
import { HeroCarousel } from '@/components/hub/HeroCarousel';
import { HubHeader } from '@/components/hub/HubHeader';
import { ResourceCarousel } from '@/components/hub/ResourceCarousel';
import { ResourceTile } from '@/components/hub/ResourceTile';
import { StreakSheet } from '@/components/hub/StreakSheet';
import { categoryOrder, localResources, type WebResource } from '@/content/resources';
import { useLearnerCatalog } from '@/hooks/useLearnerCatalog';
import { useStreaks } from '@/hooks/useStreaks';
import { collapseSplitResources } from '@/lib/hub-sections';
import {
  defaultSortForSection,
  PREMIUM_SECTION_TITLE,
  resourcesForSection,
  sortOptionsForSection,
  sortSectionResources,
  tileBadgeForSection,
  WHATS_NEW_SECTION_TITLE,
  type HubSortOption,
} from '@/lib/hub-sections';
import { mergeWebResources } from '@/lib/learner-catalog';
import { learnerApiJson } from '@/lib/learner-api';
import { track } from '@/lib/analytics';
import { useLearnerPreferences } from '@/providers/LearnerPreferences';
import { enqueueActivity } from '@/lib/progress-queue';
import { buildWebHeroSlides } from '@/lib/web-hero-slides';
import {
  POPULAR_RANK_FETCH_LIMIT,
  POPULAR_ROW_LIMIT,
  resolveWebPopularFromCatalog,
  type PopularResourceApiItem,
} from '@/lib/web-popular-resources';
import {
  categorySlug,
  hubSectionFromSlug,
  POPULAR_SECTION_TITLE,
  resourceForHref,
  resourceKeyForWebResource,
} from '@/lib/web-resource-href';
import { typeScale } from '@/lib/typography';

type FeaturedItem = {
  resource_key: string;
  sort_order: number;
  hero_image_url: string | null;
  title_override: string | null;
};

type ResourcesSearch = { section?: string };

export const Route = createFileRoute('/app/resources')({
  component: ResourcesPage,
  validateSearch: (search: Record<string, unknown>): ResourcesSearch => ({
    section: typeof search.section === 'string' ? search.section : undefined,
  }),
});

export function ResourcesPage() {
  const { section } = Route.useSearch();
  const { dialect, baseLanguage } = useLearnerPreferences();
  const mode = dialect === 'moob' ? 'moob' : 'hmonglus';
  const { streak, leaderboard } = useStreaks();
  const [streakOpen, setStreakOpen] = useState(false);
  const flashcards = useLearnerCatalog('/v1/flashcard-sets', mode, baseLanguage, 'Flashcards', '/app/flashcards');
  const sheets = useLearnerCatalog('/v1/term-sheets', mode, baseLanguage, 'Grammar', '/app/sheets');
  const stories = useLearnerCatalog('/v1/storybooks', mode, baseLanguage, 'Storybooks', '/app/storybooks');
  const featured = useQuery({
    queryKey: ['featured-resources'],
    queryFn: () => learnerApiJson<{ items: FeaturedItem[] }>(null, '/v1/resources/featured'),
    staleTime: 5 * 60_000,
    retry: 1,
  });
  const popular = useQuery({
    queryKey: ['popular-resources'],
    queryFn: () =>
      learnerApiJson<{ items: PopularResourceApiItem[] }>(
        null,
        `/v1/resources/popular?limit=${POPULAR_RANK_FETCH_LIMIT}`,
      ),
    staleTime: 5 * 60_000,
    retry: 1,
  });
  const resources = collapseSplitResources(
    mergeWebResources(localResources, flashcards.data, sheets.data, stories.data),
  );

  const selected = hubSectionFromSlug(section);
  const popularResources = resolveWebPopularFromCatalog(popular.data?.items ?? [], resources);
  const popularRow = popularResources.slice(0, POPULAR_ROW_LIMIT);
  const popularityIndex = useMemo(
    () => new Map(popularResources.map((item, index) => [item.href, index])),
    [popularResources],
  );
  const whatsNew = resourcesForSection({ kind: 'whats-new', title: WHATS_NEW_SECTION_TITLE }, resources, popularResources);
  const premium = resourcesForSection({ kind: 'premium', title: PREMIUM_SECTION_TITLE }, resources, popularResources);

  const openResource = (resource: WebResource) => {
    track('Learning Hub Card Press', { resource: resource.title, category: resource.category });
    void enqueueActivity('resource_tap', {
      resource_key: resourceKeyForWebResource(resource),
      category: resource.category,
    });
  };

  const selectedTitle = selected?.title;
  const [sort, setSort] = useState<HubSortOption>(defaultSortForSection(selectedTitle ?? 'Flashcards'));
  useEffect(() => {
    setSort(defaultSortForSection(selectedTitle ?? 'Flashcards'));
  }, [selectedTitle]);
  useEffect(() => {
    if (!selectedTitle) return;
    track('Learning Hub Section Screen', { section: selectedTitle, mode });
  }, [selectedTitle, mode]);

  const heroSlides = buildWebHeroSlides(featured.data?.items ?? [], resources);

  if (selected) {
    const unsorted = resourcesForSection(selected, resources, popularResources);
    const items = sortSectionResources(unsorted, sort, popularityIndex);
    const options = sortOptionsForSection(selected.title);
    return (
      <div className="pb-10">
        <Seo
          title={`${selected.title} — Hmonglus`}
          description={`${selected.title} in the Hmonglus learning hub — flashcards, stories, grammar, and games.`}
          path={section ? `/app/resources?section=${section}` : '/app/resources'}
        />
        <div className="flex items-center justify-between px-5 pt-6 sm:px-8">
          <Link to="/app/resources" className="font-bold text-[#2d7a13]">
            ← Learning Hub
          </Link>
        </div>
        <h1 className={`mt-4 px-5 sm:px-8 ${typeScale.screenTitle}`}>{selected.title}</h1>
        <p className="mt-1 px-5 text-sm font-semibold text-slate-500 sm:px-8">
          {items.length} {items.length === 1 ? 'activity' : 'activities'}
        </p>
        <div className="mt-4 flex flex-wrap gap-2 px-5 sm:px-8">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setSort(option.value)}
              className={`rounded-full px-3 py-1.5 text-sm font-bold ${
                sort === option.value ? 'bg-[#399918] text-white' : 'bg-white text-slate-600'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
        <div className="mt-5 grid grid-cols-2 gap-2 px-5 sm:grid-cols-3 sm:px-8 lg:grid-cols-4">
          {items.map((resource) => (
            <ResourceTile
              key={resource.href}
              resource={resource}
              widthClass="w-full"
              badge={tileBadgeForSection(selected.title, resource, popularResources.length > 0)}
              onOpen={() => openResource(resource)}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="pb-10 pt-6">
      <Seo
        title="Learning Hub — Hmonglus"
        description="Hmong flashcards, stories, grammar, pronunciation, songs, and games."
        path="/app/resources"
      />
      <HubHeader streak={streak} onStreakPress={() => setStreakOpen(true)} />
      <StreakSheet open={streakOpen} streak={streak} leaderboard={leaderboard} onClose={() => setStreakOpen(false)} />
      <div className="mt-5">
        <HeroCarousel
          slides={heroSlides}
          onOpen={(slide) => {
            const resource = resourceForHref(slide.href, resources);
            if (resource) openResource(resource);
            track('Learning Hub Hero Press', {
              resource: slide.href,
              title: slide.title,
              category: slide.category,
            });
          }}
        />
      </div>
      {whatsNew.length ? (
        <ResourceCarousel
          title={WHATS_NEW_SECTION_TITLE}
          section="whats-new"
          items={whatsNew}
          onOpen={openResource}
          badge="NEW"
        />
      ) : null}
      <ResourceCarousel
        title={POPULAR_SECTION_TITLE}
        section="popular"
        items={popularRow}
        onOpen={openResource}
        badge={POPULAR_SECTION_TITLE}
      />
      {premium.length ? (
        <ResourceCarousel
          title={PREMIUM_SECTION_TITLE}
          section="premium"
          items={premium}
          onOpen={openResource}
        />
      ) : null}
      {categoryOrder.map((category) => (
        <ResourceCarousel
          key={category}
          title={category}
          section={categorySlug(category)}
          items={resources.filter((resource) => resource.category === category)}
          onOpen={openResource}
        />
      ))}
      {(flashcards.isError || sheets.isError || stories.isError) && (
        <p className="mx-5 mt-8 rounded-2xl bg-amber-50 p-4 text-sm text-amber-900 sm:mx-8">
          Cloud lessons could not be refreshed. Bundled activities are still available.
        </p>
      )}
    </div>
  );
}
